import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { PageSelector } from './PageSelector';

export interface ButtonData {
  id?: string;
  text: string;
  url: string;
  style: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  target?: '_self' | '_blank';
  order?: number;
}

interface ButtonsEditorProps {
  buttons: ButtonData[];
  onChange: (buttons: ButtonData[]) => void;
  pages: Page[];
  isLoadingPages: boolean;
  label?: string;
  currentPageComponents?: Component[];
  showSize?: boolean;
  showGhostStyle?: boolean;
}

export const ButtonsEditor: React.FC<ButtonsEditorProps> = ({
  buttons,
  onChange,
  pages,
  isLoadingPages,
  label = 'Call-to-Action Buttons',
  currentPageComponents,
  showSize = false,
  showGhostStyle = false,
}) => {
  const addButton = () => {
    const newButton: ButtonData = {
      id: crypto.randomUUID(),
      text: 'Get Started',
      url: '/',
      style: 'primary',
      target: '_self',
      ...(showSize ? { size: 'medium' as const } : {}),
      order: buttons.length,
    };
    onChange([...buttons, newButton]);
  };

  const updateButton = (index: number, updates: Partial<ButtonData>) => {
    const newButtons = [...buttons];
    newButtons[index] = {
      ...newButtons[index],
      ...updates,
    };
    onChange(newButtons);
  };

  const removeButton = (index: number) => {
    onChange(buttons.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <button
          type="button"
          onClick={addButton}
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
        >
          <Plus size={16} />
          <span>Add Button</span>
        </button>
      </div>

      <div className="space-y-4">
        {buttons.map((button, index) => (
          <ButtonEditorCard
            key={button.id || index}
            button={button}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
            showSize={showSize}
            showGhostStyle={showGhostStyle}
            onUpdate={updates => updateButton(index, updates)}
            onRemove={() => removeButton(index)}
          />
        ))}
      </div>
    </div>
  );
};

interface ButtonEditorCardProps {
  button: ButtonData;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  showSize: boolean;
  showGhostStyle: boolean;
  onUpdate: (updates: Partial<ButtonData>) => void;
  onRemove: () => void;
}

const ButtonEditorCard: React.FC<ButtonEditorCardProps> = ({
  button,
  pages,
  isLoadingPages,
  currentPageComponents,
  showSize,
  showGhostStyle,
  onUpdate,
  onRemove,
}) => {
  const isSectionLink = button.url.startsWith('#');

  const handleUrlChange = (url: string) => {
    const updates: Partial<ButtonData> = { url };
    if (url.startsWith('#') && button.target === '_blank') {
      updates.target = '_self';
    }
    onUpdate(updates);
  };

  const visibleFieldCount = 1 + (showSize ? 1 : 0) + (isSectionLink ? 0 : 1);

  return (
    <div className="p-4 border border-gray-200 rounded-lg space-y-3 bg-gray-50">
      <div className="flex space-x-2 items-start">
        <div className="flex-1 space-y-2">
          <label className="block text-xs font-medium text-gray-600">Button Text</label>
          <input
            type="text"
            value={button.text}
            onChange={e => onUpdate({ text: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white"
            placeholder="e.g., Get Started"
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="mt-6 p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
          title="Delete button"
        >
          <Trash2 size={18} />
        </button>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">Button URL</label>
        <PageSelector
          value={button.url}
          onChange={handleUrlChange}
          pages={pages}
          isLoadingPages={isLoadingPages}
          currentPageComponents={currentPageComponents}
        />
      </div>

      <div className={`grid gap-3 grid-cols-${visibleFieldCount}`}>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Style</label>
          <select
            value={button.style}
            onChange={e => onUpdate({ style: e.target.value as ButtonData['style'] })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white"
          >
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="outline">Outline</option>
            {showGhostStyle && <option value="ghost">Ghost</option>}
          </select>
        </div>

        {showSize && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Size</label>
            <select
              value={button.size || 'medium'}
              onChange={e => onUpdate({ size: e.target.value as ButtonData['size'] })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
        )}

        {!isSectionLink && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Open In</label>
            <select
              value={button.target || '_self'}
              onChange={e => onUpdate({ target: e.target.value as ButtonData['target'] })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="_self">Same Tab</option>
              <option value="_blank">New Tab</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};
